'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Monitor, ShoppingCart, Search, Palette, Code2, Smartphone, ArrowRight, Sparkles } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const services = [
  {
    id: 'svc-web-design',
    slug: 'web-design',
    icon: Palette,
    title: 'Web Design',
    description: 'Pixel-perfect, conversion-focused designs that captivate your audience and reflect your brand identity with precision.',
    tags: ['Figma', 'UI/UX', 'Prototyping', 'Branding'],
    color: 'from-primary/20 to-primary/5',
    iconColor: 'text-primary',
    borderColor: 'group-hover:border-primary/50',
    featured: false,
  },
  {
    id: 'svc-web-dev',
    slug: 'web-development',
    icon: Code2,
    title: 'Web Development',
    description: 'High-performance websites and web apps built with modern tech stacks — fast, secure, and built to scale.',
    tags: ['Next.js', 'React', 'TypeScript', 'Node.js'],
    color: 'from-accent/20 to-accent/5',
    iconColor: 'text-accent',
    borderColor: 'group-hover:border-accent/50',
    featured: true,
  },
  {
    id: 'svc-ecommerce',
    slug: 'e-commerce',
    icon: ShoppingCart,
    title: 'E-Commerce',
    description: 'Revenue-generating online stores with seamless checkout, inventory management, and payment integrations.',
    tags: ['Shopify', 'WooCommerce', 'Stripe', 'Custom'],
    color: 'from-emerald/20 to-emerald/5',
    iconColor: 'text-emerald',
    borderColor: 'group-hover:border-emerald/50',
    featured: false,
  },
  {
    id: 'svc-seo',
    slug: 'seo-performance',
    icon: Search,
    title: 'SEO & Performance',
    description: 'Rank higher, load faster. Technical SEO, Core Web Vitals optimization, and content strategy that drives organic growth.',
    tags: ['Technical SEO', 'Core Web Vitals', 'Analytics', 'Schema'],
    color: 'from-amber/20 to-amber/5',
    iconColor: 'text-amber',
    borderColor: 'group-hover:border-amber/50',
    featured: false,
  },
  {
    id: 'svc-uiux',
    slug: 'ui-ux-design',
    icon: Monitor,
    title: 'UI/UX Design',
    description: 'Research-driven design systems and user experiences that reduce friction, increase engagement, and delight users.',
    tags: ['User Research', 'Wireframing', 'Design Systems', 'A/B Testing'],
    color: 'from-secondary/20 to-secondary/5',
    iconColor: 'text-secondary',
    borderColor: 'group-hover:border-secondary/50',
    featured: false,
  },
  {
    id: 'svc-mobile',
    slug: 'mobile-apps',
    icon: Smartphone,
    title: 'Mobile Apps',
    description: 'Cross-platform mobile applications that deliver native-quality experiences on iOS and Android.',
    tags: ['React Native', 'Expo', 'iOS', 'Android'],
    color: 'from-rose/20 to-rose/5',
    iconColor: 'text-rose',
    borderColor: 'group-hover:border-rose/50',
    featured: false,
  },
];

export default function ServicesSection() {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  return (
    <section id="services" className="py-24 relative">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="section-label">What We Do</span>
          <h2 className="font-display font-bold text-section-title text-foreground mt-3 mb-4">
            Services Built for{' '}
            <span className="text-gradient-primary">Results</span>
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto text-base">
            From strategy to launch, we provide end-to-end digital services that transform your vision into high-performing digital products.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-5">
          {services?.map((service) => {
            const Icon = service?.icon;
            const isHovered = hoveredId === service?.id;
            return (
              <div
                key={service?.id}
                className={`relative group glass-card rounded-2xl p-6 border transition-all duration-300 ${service?.borderColor} ${
                  service?.featured ? 'border-accent/30 ring-1 ring-accent/20' : 'border-border/40'
                } ${isHovered ? 'transform -translate-y-1' : ''}`}
                onMouseEnter={() => setHoveredId(service?.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                {service?.featured && (
                  <div className="absolute -top-3 left-6 flex items-center gap-1.5 bg-accent text-accent-foreground text-[10px] font-bold font-display px-3 py-1 rounded-full">
                    <Sparkles size={10} />
                    Most Popular
                  </div>
                )}
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${service?.color} flex items-center justify-center mb-5`}>
                  <Icon size={22} className={service?.iconColor} />
                </div>
                <h3 className="font-display font-bold text-lg text-foreground mb-3">{service?.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5">{service?.description}</p>
                <div className="flex flex-wrap gap-2 mb-5">
                  {service?.tags?.map((tag) => (
                    <span key={`${service?.id}-tag-${tag}`} className="text-[11px] font-mono-code px-2.5 py-1 bg-muted/50 border border-border/40 rounded-full text-muted-foreground">
                      {tag}
                    </span>
                  ))}
                </div>
                <Link
                  href={`/services/${service?.slug}`}
                  className="flex items-center gap-1.5 text-sm font-semibold font-display text-muted-foreground group-hover:text-accent transition-colors duration-200"
                >
                  Learn more <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-200" />
                </Link>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-muted-foreground text-sm mb-4">Need something custom? We do that too.</p>
          <a href="#contact" className="btn-outline text-sm">
            Discuss Your Project <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </section>
  );
}